import React, { useEffect, useState } from 'react';
import { Modal, Accordion } from 'react-bootstrap';
import { AxiosInstance } from '../baseUrl';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';

const COLOR_META = {
    green:  { badge: '#33B679', bg: '#0a1f12', dim: '#33B67922', name: 'Зелений'    },
    blue:   { badge: '#4a90d9', bg: '#0a1628', dim: '#4a90d922', name: 'Синій'      },
    purple: { badge: '#9b59b6', bg: '#160d24', dim: '#9b59b622', name: 'Фіолетовий' },
};

const COLORS = [
    { value: 'green',  label: 'Зел',  dot: '#33B679' },
    { value: 'blue',   label: 'Синій', dot: '#4a90d9' },
    { value: 'purple', label: 'Фіол', dot: '#9b59b6' },
];

const toInput = (date) => date ? format(new Date(date), "yyyy-MM-dd'T'HH:mm") : '';

export default function EventDetailModal({ show, event, onHide, onDone, onRefresh }) {
    const [detail,       setDetail]       = useState(null);
    const [loading,      setLoading]      = useState(false);

    // Edit mode state
    const [editing,        setEditing]        = useState(false);
    const [editTitle,      setEditTitle]      = useState('');
    const [editTitleMode,  setEditTitleMode]  = useState('select');
    const [editColor,      setEditColor]      = useState('green');
    const [editStart,      setEditStart]      = useState('');
    const [editSeries,     setEditSeries]     = useState('');
    const [editSeriesMode, setEditSeriesMode] = useState('select');
    const [seriesOptions,  setSeriesOptions]  = useState([]);
    const [editTitles,     setEditTitles]     = useState([]);
    const [applyToPattern, setApplyToPattern] = useState(false);
    const [editLoading,    setEditLoading]    = useState(false);

    useEffect(() => {
        if (!event) return;
        setEditing(false);
        setLoading(true);
        setDetail(null);
        AxiosInstance.get(`/events/${event.id}`)
            .then(r => setDetail(r.data))
            .finally(() => setLoading(false));
    }, [event]);

    const startEdit = () => {
        const d = detail || event;
        setEditTitle(d.title);
        setEditTitleMode('select');
        setEditColor(d.color_type);
        setEditStart(toInput(d.start_time));
        const sid = d.series_id ? String(d.series_id) : '';
        setEditSeries(sid);
        setEditSeriesMode('select');
        setApplyToPattern(false);
        setEditing(true);
        AxiosInstance.get('/events/series').then(r => setSeriesOptions(r.data)).catch(() => {});
        AxiosInstance.get('/events/titles').then(r => {
            setEditTitles(r.data);
            if (!r.data.includes(d.title)) setEditTitleMode('custom');
        }).catch(() => {});
    };

    const cancelEdit = () => setEditing(false);

    const saveEdit = async () => {
        if (!editTitle.trim() || !editStart) return;
        setEditLoading(true);
        try {
            const d = detail || event;
            const endDate = new Date(editStart);
            endDate.setHours(endDate.getHours() + 1);

            const payload = {
                title:            editTitle,
                color_type:       editColor,
                start_time:       editStart + ':00',
                end_time:         toInput(endDate) + ':00',
                apply_to_pattern: applyToPattern,
            };

            if (editSeries) payload.series_id = parseInt(editSeries, 10);

            await AxiosInstance.put(`/events/${event.id}`, payload);
            setEditing(false);
            onRefresh();
            onHide();
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалося зберегти', background: '#1a1a1a', color: '#e8e8e8' });
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (deleteFollowing) => {
        const { isConfirmed } = await Swal.fire({
            title: deleteFollowing ? 'Видалити всі наступні?' : 'Видалити подію?',
            icon: 'warning', showCancelButton: true,
            confirmButtonText: 'Так', cancelButtonText: 'Ні',
            background: '#1a1a1a', color: '#e8e8e8',
        });
        if (!isConfirmed) return;
        await AxiosInstance.delete(`/events/${event.id}`, { params: { delete_following: deleteFollowing } });
        onRefresh(); onHide();
    };

    const handlePause = async () => {
        await AxiosInstance.put(`/events/${event.id}`, { is_paused: !event.is_paused });
        onRefresh(); onHide();
    };

    const d  = detail || event;
    const cm = COLOR_META[d.color_type] || COLOR_META.green;
    const lessonNum = detail?.lesson_number ?? d.lesson_number;

    return (
        <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 bg-transparent shadow-none">
            <div style={{ background: '#141414', borderRadius: 14, overflow: 'hidden', border: '1px solid #242424', boxShadow: '0 24px 64px rgba(0,0,0,0.7)' }}>

                {/* Color accent bar */}
                <div style={{ height: 3, background: `linear-gradient(90deg, ${cm.badge} 0%, ${cm.badge}44 100%)` }} />

                {/* Header */}
                <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #1e1e1e', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: cm.bg, border: `1px solid ${cm.badge}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: cm.badge, display: 'block' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ color: '#f0f0f0', fontWeight: 700, fontSize: 16 }}>{d.title}</span>
                            {lessonNum && (
                                <span style={{ background: cm.bg, border: `1px solid ${cm.badge}55`, color: cm.badge, borderRadius: 20, padding: '1px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    Заняття #{lessonNum}
                                </span>
                            )}
                            {d.is_done   && <StatusPill color="#33B679">Виконано</StatusPill>}
                            {d.is_paused && <StatusPill color="#f39c12">Пауза</StatusPill>}
                        </div>
                        <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>
                            {format(new Date(d.start_time), 'EEEE, d MMMM yyyy · HH:mm', { locale: uk })} – {format(new Date(d.end_time), 'HH:mm', { locale: uk })}
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                            <MetaChip label="Тип" value={cm.name} color={cm.badge} />
                            {d.final_price && <MetaChip label="Ціна" value={`${d.final_price} ₴`} color="#888" />}
                            {d.series_id && <MetaChip label="ID" value={`#${d.series_id}`} color="#888" />}
                        </div>
                    </div>
                    <button onClick={onHide} style={{ background: 'none', border: 'none', color: '#3a3a3a', fontSize: 22, cursor: 'pointer', lineHeight: 1, flexShrink: 0, padding: 0, transition: 'color 0.1s' }}
                        onMouseEnter={e => e.target.style.color = '#888'} onMouseLeave={e => e.target.style.color = '#3a3a3a'}>
                        ×
                    </button>
                </div>

                {/* Body */}
                {editing ? (
                    <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                        {/* Title — select from existing titles or type custom */}
                        <EditField label="Назва">
                            <select
                                value={editTitleMode === 'custom' ? '__custom__' : editTitle}
                                onChange={e => {
                                    if (e.target.value === '__custom__') {
                                        setEditTitleMode('custom'); setEditTitle('');
                                    } else {
                                        setEditTitleMode('select'); setEditTitle(e.target.value);
                                    }
                                }}
                                style={{ ...editInput, color: (editTitle || editTitleMode === 'custom') ? '#e8e8e8' : '#555' }}
                            >
                                <option value="">— оберіть учня —</option>
                                {editTitles.map(t => <option key={t} value={t}>{t}</option>)}
                                <option value="__custom__">Інша назва...</option>
                            </select>
                            {editTitleMode === 'custom' && (
                                <input
                                    value={editTitle} onChange={e => setEditTitle(e.target.value)}
                                    placeholder="Введіть назву"
                                    style={{ ...editInput, marginTop: 6 }}
                                />
                            )}
                        </EditField>

                        {/* Color */}
                        <EditField label="Тип заняття">
                            <div style={{ display: 'flex', gap: 6 }}>
                                {COLORS.map(c => (
                                    <button key={c.value} type="button" onClick={() => setEditColor(c.value)} style={{
                                        padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                        background: editColor === c.value ? `${c.dot}22` : '#0f0f0f',
                                        border: editColor === c.value ? `2px solid ${c.dot}` : '2px solid #2e2e2e',
                                        color: editColor === c.value ? c.dot : '#777',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                    }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, display: 'inline-block' }} />
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </EditField>

                        {/* Start time */}
                        <EditField label="Початок">
                            <input
                                type="datetime-local" value={editStart}
                                onChange={e => setEditStart(e.target.value)}
                                style={editInput}
                            />
                            <div style={{ color: '#333', fontSize: 11, marginTop: 3 }}>тривалість 1 год</div>
                        </EditField>

                        {/* Series ID */}
                        <EditField label="ID серії">
                            <select
                                value={editSeriesMode === 'manual' ? '__new__' : editSeries}
                                onChange={e => {
                                    if (e.target.value === '__new__') {
                                        setEditSeriesMode('manual');
                                        setEditSeries('');
                                    } else {
                                        setEditSeriesMode('select');
                                        setEditSeries(e.target.value);
                                    }
                                }}
                                style={{ ...editInput, width: '100%' }}
                            >
                                <option value="">— без серії —</option>
                                {seriesOptions.map(s => (
                                    <option key={s.series_id} value={String(s.series_id)}>
                                        #{s.series_id} — {s.title}
                                    </option>
                                ))}
                                <option value="__new__">Нова серія (ввести вручну)...</option>
                            </select>
                            {editSeriesMode === 'manual' && (
                                <input
                                    type="number" min="1" value={editSeries}
                                    onChange={e => setEditSeries(e.target.value)}
                                    placeholder="ID серії"
                                    style={{ ...editInput, width: 140, marginTop: 6 }}
                                />
                            )}
                        </EditField>

                        {/* Apply ALL changes to all recurring events */}
                        {(event.recurrence_rule || event.pattern_id) && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, background: applyToPattern ? '#5865f211' : 'transparent', border: `1px solid ${applyToPattern ? '#5865f244' : '#2e2e2e'}`, transition: 'all 0.15s' }}>
                                <input
                                    type="checkbox" checked={applyToPattern}
                                    onChange={e => setApplyToPattern(e.target.checked)}
                                    style={{ accentColor: '#5865f2' }}
                                />
                                <span style={{ color: applyToPattern ? '#5865f2' : '#666', fontSize: 12 }}>
                                    Застосувати зміни до всіх занять цього повторення
                                    <span style={{ color: '#444', display: 'block', fontSize: 10, marginTop: 2 }}>
                                        назва учня, колір, ID серії
                                    </span>
                                </span>
                            </label>
                        )}
                    </div>
                ) : (
                    <div style={{ padding: '18px 22px', maxHeight: '58vh', overflowY: 'auto' }}>
                        {loading ? (
                            <div style={{ color: '#444', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>Завантаження...</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                                {detail?.prev_note?.next_topics && (
                                    <NoteCard accentColor="#5865f2" label="Тема цього заняття" sublabel="план з минулого заняття">
                                        <HighlightBlock color="#5865f2">{detail.prev_note.next_topics}</HighlightBlock>
                                    </NoteCard>
                                )}

                                {detail?.prev_note?.homework && (
                                    <NoteCard accentColor="#c471ed" label="Домашнє завдання" sublabel="з минулого заняття">
                                        <HighlightBlock color="#c471ed">{detail.prev_note.homework}</HighlightBlock>
                                    </NoteCard>
                                )}

                                {d.what_happened && (
                                    <NoteCard accentColor={cm.badge} label="Це заняття" sublabel="підсумок">
                                        <NoteField label="Що відбулось" value={d.what_happened} />
                                        <NoteField label="Пройдені теми" value={d.done_topics} />
                                        <NoteField label="Домашнє завдання" value={d.homework} />
                                        {d.next_topics && (
                                            <div style={{ marginTop: 4 }}>
                                                <div style={{ color: '#555', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>План на наступне заняття</div>
                                                <HighlightBlock color="#5865f2">{d.next_topics}</HighlightBlock>
                                            </div>
                                        )}
                                    </NoteCard>
                                )}

                                {detail?.prev_note && (detail.prev_note.what_happened || detail.prev_note.done_topics) && (
                                    <NoteCard accentColor="#2e2e2e" label="Минуле заняття" sublabel="для довідки">
                                        <NoteField label="Що пройшли" value={detail.prev_note.done_topics} />
                                        <NoteField label="Що відбулось" value={detail.prev_note.what_happened} />
                                    </NoteCard>
                                )}

                                {d.color_type === 'purple' && detail?.students?.length > 0 && (
                                    <NoteCard accentColor="#9b59b6" label="Учні" sublabel={`${detail.students.length} учн.`}>
                                        <Accordion flush>
                                            {detail.students.map((s, i) => (
                                                <Accordion.Item key={s.id} eventKey={String(i)} style={{ background: 'transparent', border: '1px solid #222', borderRadius: 8, marginBottom: 4, overflow: 'hidden' }}>
                                                    <Accordion.Header style={{ '--bs-accordion-btn-bg': '#0c0c0c', '--bs-accordion-active-bg': '#0c0c0c' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <span style={{ color: '#e0e0e0', fontSize: 13, fontWeight: 500 }}>{s.name}</span>
                                                            {s.mood && <span style={{ background: '#9b59b622', border: '1px solid #9b59b644', color: '#9b59b6', borderRadius: 12, padding: '1px 8px', fontSize: 11 }}>{s.mood}</span>}
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body style={{ background: '#0c0c0c', color: '#777', fontSize: 13, borderTop: '1px solid #1a1a1a' }}>
                                                        {s.homework_note || <span style={{ color: '#333' }}>Нотаток немає</span>}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            ))}
                                        </Accordion>
                                    </NoteCard>
                                )}

                                {!loading && !detail?.prev_note && !d.what_happened && !(d.color_type === 'purple' && detail?.students?.length > 0) && (
                                    <div style={{ color: '#2e2e2e', textAlign: 'center', padding: '32px 0', fontSize: 14 }}>
                                        Заняття ще не відбулось
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Footer */}
                {editing ? (
                    <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #1a1a1a', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <ActionBtn color="#555" onClick={cancelEdit}>Скасувати</ActionBtn>
                        <ActionBtn color="#5865f2" fill onClick={saveEdit}>{editLoading ? 'Збереження...' : 'Зберегти'}</ActionBtn>
                    </div>
                ) : (
                    <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #1a1a1a', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <ActionBtn color="#888" onClick={startEdit}>Редагувати</ActionBtn>
                        {!d.is_done && (
                            <ActionBtn fill color="#33B679" onClick={() => onDone(event)}>Відмітити виконаним</ActionBtn>
                        )}
                        <ActionBtn color="#f39c12" onClick={handlePause}>
                            {d.is_paused ? 'Зняти паузу' : 'Пауза'}
                        </ActionBtn>
                        <ActionBtn color="#e74c3c" onClick={() => handleDelete(false)}>Видалити</ActionBtn>
                        {d.recurrence_rule && (
                            <ActionBtn color="#e74c3c" onClick={() => handleDelete(true)}>Всі наступні</ActionBtn>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}

const StatusPill = ({ color, children }) => (
    <span style={{ background: `${color}18`, border: `1px solid ${color}44`, color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
        {children}
    </span>
);

const MetaChip = ({ label, value, color }) => (
    <div style={{ background: '#0f0f0f', border: '1px solid #2a2a2a', borderRadius: 6, padding: '3px 10px', fontSize: 11, display: 'flex', gap: 5, alignItems: 'center' }}>
        <span style={{ color: '#444' }}>{label}</span>
        <span style={{ color }}>{value}</span>
    </div>
);

const EditField = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ color: '#555', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
        {children}
    </div>
);

const NoteCard = ({ accentColor, label, sublabel, children }) => (
    <div style={{ background: '#0f0f0f', borderRadius: 10, border: `1px solid #1e1e1e`, borderLeft: `3px solid ${accentColor}`, overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #161616' }}>
            <span style={{ color: '#c0c0c0', fontWeight: 600, fontSize: 12 }}>{label}</span>
            {sublabel && <span style={{ color: '#333', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>{sublabel}</span>}
        </div>
        <div style={{ padding: '10px 14px' }}>{children}</div>
    </div>
);

const NoteField = ({ label, value }) => value ? (
    <div style={{ marginBottom: 8 }}>
        <div style={{ color: '#444', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 }}>{label}</div>
        <div style={{ color: '#b8b8b8', fontSize: 13, lineHeight: 1.55 }}>{value}</div>
    </div>
) : null;

const HighlightBlock = ({ color, children }) => (
    <div style={{ background: `${color}0e`, border: `1px solid ${color}33`, borderRadius: 7, padding: '8px 12px', color: '#d0d8f8', fontSize: 13, lineHeight: 1.55 }}>
        {children}
    </div>
);

const ActionBtn = ({ color, fill, onClick, children }) => (
    <button onClick={onClick} style={{
        padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
        border: fill ? 'none' : `1px solid ${color}55`,
        background: fill ? color : `${color}0d`,
        color: fill ? '#fff' : color,
        transition: 'opacity 0.12s',
    }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
        {children}
    </button>
);

const editInput = {
    background: '#0f0f0f', border: '1px solid #2e2e2e', color: '#e8e8e8',
    borderRadius: 8, padding: '9px 12px', fontSize: 13, width: '100%',
    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
